import React, { useState, useEffect, useCallback } from 'react';
import { smsGroupApi } from '../api/smsApi';

// ============================================================
// [GROUP_MANAGE_V2] 수신처 그룹 관리 모달
//
// V1 과 무엇이 다른가
//   V1 : 그룹 목록을 receivers(수신처 배열)에서 역산했다.
//        → 사람이 0명인 그룹은 화면에 뜰 방법이 아예 없었다(= 그룹 추가 불가).
//        → 이름변경도 인원수만큼 PUT 을 보냈다(10명이면 10번).
//   V2 : 서버 sms_groups 테이블을 본다.
//        → 빈 그룹도 남는다. 이름변경은 서버 UPDATE 한 번.
//
// 하는 일
//   - 그룹 목록 + 인원수 표시 (0명 그룹 포함)
//   - [+ 새 그룹] : 빈 그룹 생성. 기본 이름은 현재 날짜·시각
//   - [이름변경]  : 같은 이름이 이미 있으면 두 그룹이 합쳐진다
//   - [삭제]      : 관리자만. 사람은 안 지우고 '미지정' 으로 옮긴다
//
// props 는 V1 과 동일하게 유지 (receivers 는 더 안 쓰지만 받아만 둔다)
//   → ReceiverManagePage.jsx 를 고치지 않아도 그대로 동작한다
//
// 연관 파일
//   api/smsApi.js                    smsGroupApi
//   backend/routes/smsReceivers.js   /groups 라우트 4개
// ============================================================

// 새 그룹 기본 이름 — "2026-09-19 14:30"
function nowLabel() {
  const d = new Date();
  const p = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())} ` +
         `${p(d.getHours())}:${p(d.getMinutes())}`;
}

// 로그인 토큰에서 등급을 읽어 삭제 버튼 노출 여부를 정한다.
// 읽기에 실패하면 false(= 버튼 숨김). 서버도 admin 을 다시 검사하므로
// 여기서 뚫려도 실제 삭제는 막힌다 — 화면은 편의용일 뿐이다.
function isAdmin() {
  try {
    const t = localStorage.getItem('token');
    if (!t) return false;
    // JWT 는 base64url 이라 - _ 를 + / 로 바꿔야 atob 가 읽는다
    const b = t.split('.')[1].replace(/-/g, '+').replace(/_/g, '/');
    return JSON.parse(decodeURIComponent(escape(atob(b)))).grade === 'admin';
  } catch (e) { return false; }
}

export default function GroupManageModal({ open, onClose, onRefresh }) {
  const [groups, setGroups]   = useState([]);
  const [loading, setLoading] = useState(false);
  const [editId, setEditId]   = useState(null);   // 이름변경 중인 그룹 id
  const [newName, setNewName] = useState("");
  const [adding, setAdding]   = useState(false);  // 새 그룹 입력칸 열림
  const [addName, setAddName] = useState("");
  const [busy, setBusy]       = useState(false);
  const [msg, setMsg]         = useState("");

  const admin = isAdmin();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await smsGroupApi.list();
      if (res.ok) setGroups(res.items || []);
      else setMsg(res.error || "목록을 불러오지 못했습니다.");
    } catch (e) {
      setMsg("목록을 불러오지 못했습니다.");
    } finally { setLoading(false); }
  }, []);

  // 모달을 열 때마다 새로 읽는다 (다른 화면에서 그룹이 바뀌었을 수 있으므로)
  useEffect(() => {
    if (open) { load(); setEditId(null); setAdding(false); setMsg(""); }
  }, [open, load]);

  if (!open) return null;

  // ── 새 그룹 만들기 ────────────────────────────────
  const openAdd = () => { setAdding(true); setAddName(nowLabel()); setEditId(null); setMsg(""); };

  const doAdd = async () => {
    const name = addName.trim();
    if (!name) { setMsg("그룹명을 입력해 주세요."); return; }
    setBusy(true);
    try {
      const res = await smsGroupApi.create({ name });
      if (res.ok) { setAdding(false); setMsg(`"${name}" 그룹을 만들었습니다.`); await load(); }
      else setMsg(res.error || "생성 실패");
    } catch (e) { setMsg("생성 중 오류가 발생했습니다."); }
    finally { setBusy(false); }
  };

  // ── 이름변경 (서버가 UPDATE 한 번으로 처리) ────────
  const startRename = (g) => { setEditId(g.id); setNewName(g.name); setAdding(false); setMsg(""); };

  const doRename = async (g) => {
    const name = newName.trim();
    if (!name) { setMsg("새 그룹명을 입력해 주세요."); return; }
    if (name === g.name) { setEditId(null); return; }

    const dup = groups.some(x => x.name === name && x.id !== g.id);
    if (dup && !window.confirm(
      `"${name}" 그룹이 이미 있습니다.\n두 그룹이 하나로 합쳐집니다. 계속할까요?`)) return;

    setBusy(true);
    try {
      const res = await smsGroupApi.update(g.id, { name });
      if (res.ok) {
        setEditId(null);
        setMsg(res.merged
          ? `"${name}" 로 합쳤습니다. (${res.moved}명 이동)`
          : `"${name}" 로 바꿨습니다. (${res.moved}명 반영)`);
        await load();
        onRefresh && onRefresh();   // 수신처 목록의 그룹명도 갱신
      } else setMsg(res.error || "변경 실패");
    } catch (e) { setMsg("변경 중 오류가 발생했습니다."); }
    finally { setBusy(false); }
  };

  // ── 삭제 (관리자만) ──────────────────────────────
  const doDelete = async (g) => {
    const warn = g.cnt > 0
      ? `"${g.name}" 그룹을 삭제합니다.\n\n소속된 ${g.cnt}명은 지워지지 않고 '미지정' 으로 옮겨집니다.\n계속할까요?`
      : `"${g.name}" 그룹을 삭제합니다. (소속 0명)\n계속할까요?`;
    if (!window.confirm(warn)) return;

    setBusy(true);
    try {
      const res = await smsGroupApi.remove(g.id);
      if (res.ok) {
        setMsg(res.moved > 0
          ? `삭제했습니다. ${res.moved}명이 '미지정' 으로 이동했습니다.`
          : `삭제했습니다.`);
        await load();
        onRefresh && onRefresh();
      } else setMsg(res.error || "삭제 실패");
    } catch (e) { setMsg("삭제 중 오류가 발생했습니다."); }
    finally { setBusy(false); }
  };

  const inputStyle = {
    width: "100%", padding: "8px 10px", border: "1px solid #cbd5e0",
    borderRadius: "6px", fontSize: "14px", boxSizing: "border-box",
  };
  const miniBtn = (bg, fg, bd) => ({
    padding: "5px 12px", background: bg, color: fg, border: `1px solid ${bd}`,
    borderRadius: "6px", cursor: "pointer", fontSize: "12px", flexShrink: 0,
  });

  return (
    <div className="modal-overlay">
      <div className="modal-card" style={{ maxWidth: "460px", width: "94%" }}>
        <div className="modal-header">
          <h3>🏷️ 그룹 관리</h3>
          <button className="close-x" onClick={onClose}>&times;</button>
        </div>

        <div className="modal-body" style={{ maxHeight: "65vh", overflowY: "auto" }}>
          {/* ── 새 그룹 추가 ── */}
          {adding ? (
            <div style={{ padding: "10px 12px", marginBottom: "10px",
                          background: "#ecfdf5", border: "1px solid #a7f3d0",
                          borderRadius: "8px" }}>
              <div style={{ fontSize: "11px", color: "#047857", marginBottom: "5px" }}>
                새 그룹 이름 (사람은 나중에 넣어도 됩니다)
              </div>
              <div style={{ display: "flex", gap: "6px" }}>
                <input style={{ ...inputStyle, flex: 1, minWidth: 0 }}
                  value={addName} autoFocus disabled={busy}
                  onChange={e => setAddName(e.target.value)}
                  onKeyDown={e => { if (e.key === "Enter") doAdd(); }} />
                <button onClick={doAdd} disabled={busy}
                  style={{ ...miniBtn("#059669", "#fff", "#059669"), fontWeight: 600 }}>
                  만들기
                </button>
                <button onClick={() => setAdding(false)} disabled={busy}
                  style={miniBtn("#fff", "#4a5568", "#e2e8f0")}>취소</button>
              </div>
            </div>
          ) : (
            <button onClick={openAdd} disabled={busy}
              style={{ width: "100%", padding: "9px", marginBottom: "12px",
                       background: "#ecfdf5", color: "#047857",
                       border: "1px dashed #6ee7b7", borderRadius: "8px",
                       cursor: "pointer", fontSize: "13px", fontWeight: 600 }}>
              ＋ 새 그룹 추가
            </button>
          )}

          <p style={{ fontSize: "12px", color: "#94a3b8", marginBottom: "12px" }}>
            이름을 바꾸면 그 그룹의 전원이 함께 변경됩니다.
            이미 있는 이름으로 바꾸면 두 그룹이 합쳐집니다.
          </p>

          {loading && (
            <p style={{ textAlign: "center", color: "#94a3b8", padding: "20px 0" }}>
              불러오는 중...
            </p>
          )}

          {!loading && groups.length === 0 && (
            <p style={{ textAlign: "center", color: "#94a3b8", padding: "20px 0" }}>
              등록된 그룹이 없습니다.
            </p>
          )}

          {groups.map(g => (
            <div key={g.id} style={{
              padding: "10px 12px", marginBottom: "8px",
              background: editId === g.id ? "#f0f9ff" : "#f8fafc",
              border: "1px solid #e2e8f0", borderRadius: "8px",
            }}>
              {editId === g.id ? (
                <>
                  <div style={{ fontSize: "11px", color: "#64748b", marginBottom: "5px" }}>
                    {g.name} · {g.cnt}명 → 새 이름
                  </div>
                  <div style={{ display: "flex", gap: "6px" }}>
                    <input style={{ ...inputStyle, flex: 1, minWidth: 0 }}
                      value={newName} autoFocus disabled={busy}
                      placeholder="새 그룹명"
                      onChange={e => setNewName(e.target.value)}
                      onKeyDown={e => { if (e.key === "Enter") doRename(g); }} />
                    <button onClick={() => doRename(g)} disabled={busy}
                      style={{ ...miniBtn("#2563eb", "#fff", "#2563eb"), fontWeight: 600 }}>
                      {busy ? "..." : "변경"}
                    </button>
                    <button onClick={() => setEditId(null)} disabled={busy}
                      style={miniBtn("#fff", "#4a5568", "#e2e8f0")}>취소</button>
                  </div>
                </>
              ) : (
                <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                  <span style={{ flex: 1, fontSize: "14px", fontWeight: 600,
                                 color: "#1e293b", minWidth: 0, overflow: "hidden",
                                 textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                    {g.name}
                  </span>
                  {/* 0명 그룹은 회색으로 구분 — 만들어만 두고 아직 안 쓴 그룹 */}
                  <span style={{ fontSize: "12px", flexShrink: 0,
                                 color: g.cnt > 0 ? "#64748b" : "#cbd5e0" }}>
                    {g.cnt}명
                  </span>
                  <button onClick={() => startRename(g)} disabled={busy}
                    style={miniBtn("#fff", "#2563eb", "#bfdbfe")}>이름변경</button>
                  {/* [GRP_DEL_C_UI] 0명 그룹은 누구나(premium 이상), 사람이 있으면 admin 만.
                      서버(smsReceivers.js GRP_DEL_C)가 같은 규칙을 다시 검사하므로
                      화면은 편의용이고 실제 차단은 서버가 한다. */}
                  {(admin || g.cnt === 0) && (
                    <button onClick={() => doDelete(g)} disabled={busy}
                      style={miniBtn("#fff", "#dc2626", "#fecaca")}>삭제</button>
                  )}
                </div>
              )}
            </div>
          ))}

          {msg && (
            <p style={{ marginTop: "12px", fontSize: "13px", color: "#475569",
                        textAlign: "center" }}>{msg}</p>
          )}
        </div>

        <div className="modal-footer">
          <button className="btn-cancel" onClick={onClose} disabled={busy}>닫기</button>
        </div>
      </div>
    </div>
  );
}
